import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowPathIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import Layout from '../../programs/Layout'
import { getGoLiveReadinessApi } from '../../../apis/apis'
import type {
    GoLiveReadinessVO,
    GoLiveProgramVO,
    GoLiveGateVO,
    GoLiveBucket,
    GoLiveBucketCountsVO,
    GoLiveStatusTone,
} from '../../../apis/types'
import { gateVerdictStyles, verdictOrder, statusToneStyles, ownerLabel } from './gateStyles'
import { SubmissionFlow } from './SubmissionFlow'

// Tab presets, in display order. Labels use the tenant team's own vocabulary
// (Slack): a program past PTO but not yet billing is "live" while it's recent
// and "behind" once it's aged. `bucket` matches the backend FleetBucket value;
// `countKey` indexes bucketCounts (camelCase).
const BUCKETS: { bucket: GoLiveBucket; countKey: keyof GoLiveBucketCountsVO; label: string }[] = [
    { bucket: 'recently-live', countKey: 'recentlyLive', label: 'Recently Live' },
    { bucket: 'behind', countKey: 'behind', label: 'Behind' },
    { bucket: 'billing', countKey: 'billing', label: 'Billing' },
    { bucket: 'pre-pto', countKey: 'prePto', label: 'Pre-PTO' },
]

// G_A_03 passes only when the allocation sum is in [99.99, 100.01]. A value like
// 100.03 must not read as clean green (it fails the gate), so we tint by actual
// in-band membership and show one decimal so a near-100 value is not hidden by
// rounding to "100%".
const COVERAGE_BAND_LO = 99.99
const COVERAGE_BAND_HI = 100.01

function formatCoverage(pct: number | null): { text: string; tone: 'good' | 'bad' | 'none' } {
    if (pct == null) return { text: '—', tone: 'none' }
    const inBand = pct >= COVERAGE_BAND_LO && pct <= COVERAGE_BAND_HI
    return { text: `${pct.toFixed(1)}%`, tone: inBand ? 'good' : 'bad' }
}

const GateChips: React.FC<{ gates: GoLiveGateVO[] }> = ({ gates }) => (
    <span className="inline-flex gap-1 align-middle">
        {gates.map((gate) => {
            const style = gateVerdictStyles[gate.verdict]
            const title = `${gate.label}: ${style.label}${gate.failureClass ? ` — ${gate.failureClass}` : ''}`
            return (
                <span
                    key={gate.gate}
                    title={title}
                    className={`inline-block w-3.5 h-3.5 rounded-[3px] ${style.dot}`}
                />
            )
        })}
    </span>
)

const StatusChip: React.FC<{ label: string; tone: GoLiveStatusTone }> = ({ label, tone }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ${statusToneStyles[tone]}`}>
        {label}
    </span>
)

const OwnerChip: React.FC<{ owner: string | null }> = ({ owner }) => {
    const label = ownerLabel(owner)
    return label ? (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/50">
            {label}
        </span>
    ) : null
}

const ActionChip: React.FC<{ action: GoLiveProgramVO['nextAction'] }> = ({ action }) =>
    action ? (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-400/10 ring-1 ring-inset ring-indigo-600/20">
            {action.label}
        </span>
    ) : (
        <span className="text-slate-400">—</span>
    )

const CoverageCell: React.FC<{ pct: number | null }> = ({ pct }) => {
    const cov = formatCoverage(pct)
    const cls = cov.tone === 'bad' ? 'text-red-600 dark:text-red-400' : cov.tone === 'good' ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-slate-500'
    return <span className={`tabular-nums font-semibold ${cls}`}>{cov.text}</span>
}

const ProgramRow: React.FC<{ program: GoLiveProgramVO }> = ({ program }) => {
    const [open, setOpen] = useState(false)
    return (
        <>
            <tr className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-200">
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        aria-expanded={open}
                        className="inline-flex items-center gap-1.5 text-left hover:text-slate-900 dark:hover:text-white"
                    >
                        {open ? (
                            <ChevronDownIcon className="w-3 h-3 text-slate-400 flex-none" />
                        ) : (
                            <ChevronRightIcon className="w-3 h-3 text-slate-400 flex-none" />
                        )}
                        <span>{program.name}</span>
                    </button>
                </td>
                <td className="px-4 py-2.5">{program.utility ?? '—'}</td>
                <td className="px-4 py-2.5 tabular-nums">{program.ptoDate ? program.ptoDate.slice(0, 10) : '—'}</td>
                <td className="px-4 py-2.5"><GateChips gates={program.gates} /></td>
                <td className="px-4 py-2.5"><CoverageCell pct={program.vnemCoveragePct} /></td>
                <td className="px-4 py-2.5"><OwnerChip owner={program.owner} /></td>
                <td className="px-4 py-2.5"><ActionChip action={program.nextAction} /></td>
                <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5">
                        <StatusChip label={program.statusLabel} tone={program.statusTone} />
                        {program.actionableFixCount > 0 && (
                            <span
                                title={`${program.actionableFixCount} more KE-actionable gate${program.actionableFixCount === 1 ? '' : 's'} failing behind the blocking step — expand to see them`}
                                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-400/10 ring-1 ring-inset ring-amber-600/20 dark:ring-amber-400/20"
                            >
                                +{program.actionableFixCount} to fix
                            </span>
                        )}
                    </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                    <Link
                        to={`/program/programs/${program.programCuid}/reconciliation`}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold text-xs whitespace-nowrap"
                    >
                        Open report &rarr;
                    </Link>
                </td>
            </tr>
            {open && (
                <tr>
                    <td colSpan={9} className="p-0 border-b border-slate-200 dark:border-slate-700">
                        <SubmissionFlow program={program} />
                    </td>
                </tr>
            )}
        </>
    )
}

const Kpi: React.FC<{ value: string | number; label: string; accent: string; tooltip: string }> = ({
    value,
    label,
    accent,
    tooltip,
}) => (
    <div
        title={tooltip}
        className={`flex-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-3 border-l-[3px] cursor-help ${accent}`}
    >
        <div className="text-2xl font-bold text-slate-900 dark:text-white">{value}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-0.5">
            {label}
            <span className="ml-1 normal-case text-slate-300 dark:text-slate-600" aria-hidden="true">ⓘ</span>
        </div>
    </div>
)

const Legend: React.FC = () => (
    <div className="mt-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="flex items-center gap-x-6 gap-y-2 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
            <span className="font-medium text-slate-600 dark:text-slate-300">Gate verdicts</span>
            <span className="text-slate-300 dark:text-slate-600">|</span>
            {verdictOrder.map((v) => (
                <span key={v} className="flex items-center gap-1.5">
                    <span className={`w-3 h-3 rounded-[3px] inline-block ${gateVerdictStyles[v].dot}`} />
                    {gateVerdictStyles[v].label}
                </span>
            ))}
        </div>
    </div>
)

const GoLiveReadiness: React.FC = () => {
    const [report, setReport] = useState<GoLiveReadinessVO | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [activeBucket, setActiveBucket] = useState<GoLiveBucket>('recently-live')

    const fetchReport = useCallback(async () => {
        setIsLoading(true)
        setError(null)
        try {
            const data = await getGoLiveReadinessApi()
            setReport(data)
        } catch (err) {
            setError('Failed to load go-live readiness report.')
            console.error(err)
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        void fetchReport()
    }, [fetchReport])

    // Flat program list (no property grouping): every NEM2V program in the
    // active bucket, in backend order so same-property programs stay adjacent.
    // Each row expands to its own submission flow.
    const visiblePrograms = useMemo<GoLiveProgramVO[]>(() => {
        if (!report) return []
        return report.properties.flatMap((prop) => prop.programs).filter((p) => p.bucket === activeBucket)
    }, [report, activeBucket])

    return (
        <Layout title="Go-Live Readiness">
            <div>
                {/* Breadcrumb */}
                <nav className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                    <span>Admin</span>
                    <span className="mx-1">/</span>
                    <span className="text-slate-900 dark:text-white font-medium">Go-Live Readiness</span>
                </nav>

                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-medium text-indigo-900 dark:text-white">Go-Live Readiness</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                            NEM2V fleet — readiness to bill. Stage A gates, one row per program.
                            {report?.generatedAt && (
                                <> &middot; Data queried {new Date(report.generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                            )}
                        </p>
                    </div>
                    <button
                        onClick={() => void fetchReport()}
                        disabled={isLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                    >
                        <ArrowPathIcon className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                        Refresh Data
                    </button>
                </div>

                {isLoading && !report && <div className="text-sm font-medium dark:text-white">Loading...</div>}

                {error && (
                    <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 mb-4">
                        <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
                    </div>
                )}

                {report && (
                    <>
                        {/* KPI strip */}
                        <div className="flex gap-3.5 mb-4">
                            <Kpi
                                value={report.kpis.inScope}
                                label="Programs in scope"
                                accent="border-l-indigo-600"
                                tooltip="Every NEM2V program in the fleet. The denominator for the other tiles."
                            />
                            <Kpi
                                value={report.kpis.billing}
                                label="Billing"
                                accent="border-l-green-500"
                                tooltip="Programs we are actively invoicing — at least one non-void invoice covering service on or after the PTO date. Independent of gate status."
                            />
                            <Kpi
                                value={report.kpis.worklist}
                                label="Not yet billing"
                                accent="border-l-red-500"
                                tooltip={`Past-PTO programs with no qualifying invoice yet — the go-live worklist. These are the Recently Live + Behind tabs (split at a ${report.worklistSplitDays}-day PTO age).`}
                            />
                            <Kpi
                                value={report.kpis.dataQuality}
                                label="Data-quality flags"
                                accent="border-l-blue-500"
                                tooltip="Programs that ARE billing but still fail a Stage A gate (e.g. PTO drift, VNEM↔SF mismatch). A correctness queue to reconcile — not a reason they cannot bill."
                            />
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700">
                            {BUCKETS.map(({ bucket, countKey, label }) => {
                                const isActive = bucket === activeBucket
                                const count = report.bucketCounts[countKey] ?? 0
                                return (
                                    <button
                                        key={bucket}
                                        type="button"
                                        onClick={() => setActiveBucket(bucket)}
                                        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
                                            isActive
                                                ? 'text-indigo-700 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400'
                                                : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-200'
                                        }`}
                                    >
                                        {label}
                                        <span className="ml-1.5 px-1.5 py-px rounded-full text-[11px] font-bold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                            {count}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>

                        {/* Bucket-rule caption — make the worklist split obvious */}
                        <p className="mt-2.5 mb-1 text-xs text-slate-500 dark:text-slate-400">
                            Worklist splits at a{' '}
                            <span className="font-bold text-indigo-700 dark:text-indigo-400">{report.worklistSplitDays}-day</span>{' '}
                            PTO age:{' '}
                            <span className="font-semibold text-slate-700 dark:text-slate-300">Recently Live</span> = reached PTO, not yet billing, ≤ {report.worklistSplitDays}d ·{' '}
                            <span className="font-semibold text-slate-700 dark:text-slate-300">Behind</span> = {report.worklistSplitDays}d+ past PTO, still not billing ·{' '}
                            <span className="font-semibold text-slate-700 dark:text-slate-300">Billing</span> = actively invoicing (a blue status = billing but a gate needs review) ·{' '}
                            <span className="font-semibold text-slate-700 dark:text-slate-300">Pre-PTO</span> = PTO upcoming
                        </p>

                        {/* Table */}
                        <div className="mt-4 overflow-x-auto bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                        <th className="text-left font-semibold px-4 py-2.5" style={{ width: '30%' }}>Program</th>
                                        <th className="text-left font-semibold px-4 py-2.5">Utility</th>
                                        <th className="text-left font-semibold px-4 py-2.5">PTO</th>
                                        <th className="text-left font-semibold px-4 py-2.5">Gates (A)</th>
                                        <th className="text-left font-semibold px-4 py-2.5">VNEM cov.</th>
                                        <th className="text-left font-semibold px-4 py-2.5">Owner</th>
                                        <th className="text-left font-semibold px-4 py-2.5">Action</th>
                                        <th className="text-left font-semibold px-4 py-2.5">Status</th>
                                        <th className="px-4 py-2.5" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {visiblePrograms.map((program) => (
                                        <ProgramRow key={program.programCuid} program={program} />
                                    ))}
                                    {visiblePrograms.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="px-4 py-10 text-center text-sm text-slate-400 dark:text-slate-500">
                                                No programs in this view.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Legend />

                        {report.dataLimitations && report.dataLimitations.length > 0 && (
                            <div className="mt-4 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3">
                                <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">Data Limitations</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                    {report.dataLimitations.map((msg, i) => (
                                        <li key={i} className="text-xs text-amber-700 dark:text-amber-300">{msg}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    )
}

export default GoLiveReadiness
